import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import {
    Button,
    ButtonGroup,
    Col,
    ControlLabel,
    DatePicker,
    FlexboxGrid,
    Form,
    FormControl,
    Message,
    Progress,
    SelectPicker,
    Toggle,
} from 'rsuite';
import { authHeader } from '../../../../redux/helpers';
import { axiosService, webSocketService } from '../../../../redux/services';

type Props = {
    sensors: Array<number>;
    cancelForce: Function;
    nextStep?: Function;
} & WrappedComponentProps;

type State = {
    isLoading: boolean;
    hasError: boolean;
    error: any | null;
    formValue: FormValue;
    forced: Array<number>;
};

type FormValue = {
    isForce: boolean;
    forceType: number;
    // ledMask: number; FF
    // exitLed: boolean; !isForce
    endForceTime: Date;
};

class ForceTCMSensorPart extends React.Component<Props, State> {
    forceValues: Array<any>;
    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: false,
            hasError: false,
            error: null,
            formValue: {
                isForce: false,
                forceType: 1,
                // ledMask: 0,
                // exitLed: false,
                endForceTime: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
            },
            forced: [],
        };

        this.forceValues = [
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.free',
                }),
                value: 1,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.occupied',
                }),
                value: 2,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.overstayFree',
                }),
                value: 3,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.overstayOccupied',
                }),
                value: 4,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.green',
                }),
                value: 11,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.red',
                }),
                value: 12,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.black',
                }),
                value: 13,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.greenBlink',
                }),
                value: 14,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.redBlink',
                }),
                value: 15,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'sensor.force.redGreenBlink',
                }),
                value: 17,
            },
        ];
    }

    updateStatus = e => {
        let forcedArray = this.state.forced;

        if (forcedArray.indexOf(e.id) === -1 && this.props.sensors.indexOf(e.id) !== -1) {
            forcedArray.push(e.id);

            this.setState({
                forced: forcedArray,
            });
        }
    };

    sendForce = () => {
        this.setState({
            isLoading: true,
            hasError: false,
            error: null,
        });

        webSocketService.onEvent('tcm-sensor:updateStatus', this.updateStatus);

        axiosService
            .getAxios()
            .put(
                '/devices/tcm-sensor/forceSensor',
                {
                    tabId: this.props.sensors,
                    isForce: this.state.formValue.isForce,
                    forceState: this.state.formValue.isForce ? this.state.formValue.forceType : 0,
                    endForceTime: this.state.formValue.isForce ? this.state.formValue.endForceTime : new Date(),
                    startForceTime: new Date(),
                },
                {
                    headers: authHeader(),
                }
            )
            .then(() => {
                this.setState({
                    isLoading: false,
                });

                if (this.props.nextStep) {
                    this.props.nextStep();
                } else {
                    this.props.cancelForce();
                }
            })
            .catch(err => {
                this.setState({
                    hasError: true,
                    error: err,
                });
            })
            .finally(() => {
                this.setState({
                    isLoading: false,
                    forced: [],
                });

                webSocketService.offEvent('tcm-sensor:updateStatus', this.updateStatus);
            });
    };

    cancel = () => {
        this.props.cancelForce();
    };

    handleChange = formValue => {
        this.setState({
            formValue,
        });
    };

    render = () => {
        return (
            <FlexboxGrid align="middle" justify="space-around">
                {this.state.hasError && (
                    <FlexboxGrid.Item componentClass={Col} xs={24}>
                        <Message
                            type="error"
                            description={this.props.intl.formatMessage(
                                {
                                    id: 'tcmSensors.force.error',
                                },
                                {
                                    error: this.state.error,
                                }
                            )}
                        />
                    </FlexboxGrid.Item>
                )}

                {this.state.isLoading ? (
                    <FlexboxGrid.Item componentClass={Col} xs={24}>
                        <Progress.Line
                            percent={(this.state.forced.length / this.props.sensors.length) * 100}
                            showInfo={false}
                        />

                        <p className="text-center">
                            {this.state.forced.length} / {this.props.sensors.length}
                        </p>
                    </FlexboxGrid.Item>
                ) : (
                    <FlexboxGrid.Item componentClass={Col} xs={24}>
                        <Form formValue={this.state.formValue} onChange={this.handleChange}>
                            <FlexboxGrid justify="center" align="middle">
                                <FlexboxGrid.Item>
                                    <FormControl
                                        data-cy="tcmSensor-force-isForced"
                                        name="isForce"
                                        accepter={Toggle}
                                        size="lg"
                                        checkedChildren={this.props.intl.formatMessage({
                                            id: 'tcmSensors.force.forced',
                                        })}
                                        unCheckedChildren={this.props.intl.formatMessage({
                                            id: 'tcmSensors.force.unforced',
                                        })}
                                    />
                                </FlexboxGrid.Item>
                            </FlexboxGrid>

                            {this.state.formValue.isForce && (
                                <FlexboxGrid className="margin-top-15">
                                    <FlexboxGrid.Item componentClass={Col} xs={12}>
                                        <ControlLabel>
                                            <FormattedMessage id="tcmSensors.force.type" />
                                            <FormControl
                                                data-cy="tcmSensor-force-type"
                                                name="forceType"
                                                accepter={SelectPicker}
                                                data={this.forceValues}
                                                searchable={false}
                                                cleanable={false}
                                                renderMenuItem={(label, item) => {
                                                    return (
                                                        <div data-cy={`tcmSensors-force-value-${item.value}`}>
                                                            {label}
                                                        </div>
                                                    );
                                                }}
                                            />
                                        </ControlLabel>
                                    </FlexboxGrid.Item>
                                    <FlexboxGrid.Item componentClass={Col} xs={12}>
                                        <ControlLabel>
                                            <FormattedMessage id="tcmSensors.force.endForceTime" />
                                            <FormControl
                                                name="endForceTime"
                                                accepter={DatePicker}
                                                data={this.forceValues}
                                                format="DD/MM/YYYY HH:mm:ss"
                                                cleanable={false}
                                            />
                                        </ControlLabel>
                                    </FlexboxGrid.Item>
                                </FlexboxGrid>
                            )}

                            <FlexboxGrid justify="end" align="middle">
                                <FlexboxGrid.Item>
                                    <ButtonGroup>
                                        <Button
                                            color="red"
                                            data-cy="ipcan-elements-sensor-force-cancel"
                                            onClick={() => this.cancel()}
                                            loading={this.state.isLoading}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                        <Button
                                            color="green"
                                            data-cy="ipcan-elements-sensor-force-valid"
                                            onClick={() => this.sendForce()}
                                            loading={this.state.isLoading}>
                                            <FontAwesomeIcon icon={faCheck} />
                                        </Button>
                                    </ButtonGroup>
                                </FlexboxGrid.Item>
                            </FlexboxGrid>
                        </Form>
                    </FlexboxGrid.Item>
                )}
            </FlexboxGrid>
        );
    };
}

export default injectIntl(ForceTCMSensorPart);
